import { createFormData, createHeaders } from "@quatico/magellan-client/lib/transport/formdata-fetch";
import { type ClientContext } from "@quatico/magellan-shared";

import { readIamSessionToken } from "./session-token";

/**
 * Minimal Magellan transport for IAM-CDS calls — a self-contained copy of the
 * pieces of iam-cds's `localeFetchTransport` that trexpert needs, so this module
 * carries **no dependency on `@qs/cds-iam-client`**. It POSTs to the IAM endpoint
 * with an `X-Locale` header, and an `Authorization: Bearer <token>` header when a
 * session token is present in localStorage (the login flow itself is anonymous;
 * the token only exists after a successful login). The token read is shared with
 * the trexpert transport + role-gating via `readIamSessionToken`.
 *
 * Everything IAM-specific lives under `src/iam/` so it can be lifted into a
 * dedicated `@kmu-trexpert/iam` package later without touching the host.
 */

const LOCALE = "de-CH";

export const iamFetchTransport = async (
    func: { name: string; payload: string; namespace: string; endpoint: string },
    ctx: ClientContext
): Promise<string> => {
    const { name, endpoint } = func;
    if (!name) {
        throw new Error('Cannot invoke remote function without "name" property.');
    }
    const baseHeaders: Record<string, string> = {
        ...(ctx?.client?.headers ?? {}),
        "X-Locale": LOCALE,
    };
    const hasAuthHeader = Object.keys(baseHeaders).some(key => key.toLowerCase() === "authorization");
    if (!hasAuthHeader) {
        const token = readIamSessionToken();
        if (token) {
            baseHeaders.Authorization = `Bearer ${token}`;
        }
    }
    const response = await fetch(endpoint, {
        method: "POST",
        body: createFormData(func),
        headers: createHeaders({ headers: baseHeaders }),
    });
    if (!response.ok) {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject({ status: response.status, message: response?.statusText || "" });
    }
    return response.text();
};
